import { useEffect, useRef } from "react";
import { Btn } from "../../components/ui/Button";
import { recordedLabel } from "./format";
import type { SourceDrawerContent } from "./sourceDrawerContent";
import type { LogRequest } from "./types";

interface Props {
  content: SourceDrawerContent;
  onClose: () => void;
  onOpenInLog: (logRequest: LogRequest) => void;
}

const SECTION_LABEL_CLASSES = "m-0 mb-1.5 text-[11px] uppercase tracking-wider text-rs-light-gray";

export function SourceDrawer({ content, onClose, onOpenInLog }: Props) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeButtonRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[1000] bg-[rgba(12,14,10,0.65)]" role="presentation" onClick={onClose}>
      <aside
        className="absolute top-0 right-0 bottom-0 flex flex-col w-full max-w-[420px] bg-rs-off-black border-l border-rs-jet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="source-drawer-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="flex items-start gap-3 px-6 pt-5 pb-4 border-b border-rs-jet">
          <div className="flex-1 min-w-0">
            <p className={SECTION_LABEL_CLASSES}>{content.kindLabel}</p>
            <h3 id="source-drawer-title" className="m-0 text-base text-rs-off-white break-words">
              {content.title}
            </h3>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="bg-transparent border-0 p-1 text-rs-light-gray cursor-pointer hover:text-rs-green"
            aria-label="Close"
            onClick={onClose}
          >
            <span className="material-symbols-sharp">close</span>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-6">
          <dl className="m-0 grid grid-cols-[120px_1fr] gap-x-4 gap-y-2.5 text-[13px]">
            {content.fields.map((field) => (
              <div key={field.label} className="contents">
                <dt className="text-rs-light-gray">{field.label}</dt>
                <dd className="m-0 text-rs-off-white whitespace-pre-wrap break-words">{field.value}</dd>
              </div>
            ))}
          </dl>

          {(content.sourceReference || content.sourceUrl) && (
            <div>
              <p className={SECTION_LABEL_CLASSES}>Source</p>
              {content.sourceReference && (
                <p className="m-0 text-[13px] text-rs-off-white">{content.sourceReference}</p>
              )}
              {content.sourceUrl && (
                <a
                  href={content.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[13px] text-rs-green break-all"
                >
                  {content.sourceUrl}
                </a>
              )}
            </div>
          )}

          {content.enteredByDisplayName && (
            <div>
              <p className={SECTION_LABEL_CLASSES}>Logged by</p>
              <p className="m-0 text-[13px] text-rs-off-white">
                {content.enteredByDisplayName}
                {content.recordedUtc ? ` · ${recordedLabel(content.recordedUtc)}` : ""}
              </p>
            </div>
          )}
        </div>

        <footer className="flex justify-end gap-2 px-6 py-4 border-t border-rs-jet">
          <Btn variant="ghost" onClick={onClose}>
            Close
          </Btn>
          <Btn onClick={() => onOpenInLog(content.logRequest)}>
            <span className="material-symbols-sharp">edit_note</span>
            {content.actionLabel}
          </Btn>
        </footer>
      </aside>
    </div>
  );
}
